/**
 * The questions TypeSafe's `POST /v1/systemone` accepts, as documented at
 * docs.typesafe.ai/api (read 2026-09-18). Written as plain types rather than
 * taken from `@typesafe-ai/sdk`: that package is pre-1.0 and changed its Score
 * shape underneath its own callers, and the wire is small enough to own.
 *
 * The question half of the wire lives here, beside the planner that builds it,
 * so the editor and the server judge a schema with one translator. The request
 * envelope, the response and the transport stay with the backend's Jev client.
 *
 * `instructions` accepts a string, an object or an array; this platform only
 * ever sends JSON values, so it is typed as such.
 */

export type JevJsonValue =
  | string
  | number
  | boolean
  | null
  | readonly JevJsonValue[]
  | { readonly [key: string]: JevJsonValue };

/** A yes/no question. The answer is the probability of yes. */
export interface JevNoulQuestion {
  readonly type: "noul";
  readonly instructions: JevJsonValue;
  readonly criteria?: { readonly true?: string; readonly false?: string };
}

/** Pick one option. `criteria` maps each option to a rubric line, or null. */
export interface JevChoiceQuestion {
  readonly type: "choice";
  readonly instructions: JevJsonValue;
  readonly criteria: Readonly<Record<string, string | null>>;
}

/** Rate along an ORDERED list of level descriptions. */
export interface JevScoreQuestion {
  readonly type: "score";
  readonly instructions: JevJsonValue;
  readonly criteria: readonly string[];
}

export type JevQuestion = JevNoulQuestion | JevChoiceQuestion | JevScoreQuestion;

/**
 * TypeSafe's documented per-question ceilings. A question past them is refused
 * before the request leaves the process, so the caller is told which field is
 * wrong instead of receiving a 422 about a key it never chose.
 */
export const JEV_CHOICE_MAX_OPTIONS = 255;
export const JEV_SCORE_MIN_LEVELS = 2;
export const JEV_SCORE_MAX_LEVELS = 10;
